import {
	Box,
	Button,
	Card,
	CardContent,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	API_TIME_FORMAT,
	DISPLAY_DATE_FORMAT,
	DISPLAY_TIME_FORMAT,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import LogHistory from './LogHistory';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import DeleteInterventionDialog from './components/DeleteInterventionDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
	useGetExportCustomerPDFMutation,
	useGetInterventionGenerateReportMutation,
} from '@/api/common';
import {
	useDeleteInterventionMutation,
	useDraftInterventionMutation,
	useGetInterventionDetailQuery,
	useLazyCheckInterventionRevertStatusQuery,
} from '@/api/member-management/intervention';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { CaseNoteStatusEnum, PermissionEnum } from '@/api/enum/common.enum';
import { useGetUser } from '@/hooks/useGetUser';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}

export default function InterventionDetailPage() {
	const { t } = useTranslation();
	const { interventionId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();

	// This page is mounted at several routes (case-management lists and
	// member-management member/admission-request details). Derive the route to
	// return to from the current pathname so deletion navigates correctly
	// regardless of how the page was entered (including direct URL entry).
	const getReturnRoute = () => {
		const path = location.pathname;
		// Nested under a member / admission-request detail:
		// .../detail/:admissionRequestId/intervention/detail/:interventionId
		// Return to that detail page on its Intervention tab (driven by ?tab).
		const nestedMarker = `/intervention/detail/${interventionId}`;
		if (path.startsWith('/member-management')) {
			const idx = path.indexOf(nestedMarker);
			if (idx !== -1) {
				return `${path.slice(0, idx)}?tab=intervention`;
			}
		}
		// Case-management list pages (no tabs):
		// /case-management/intervention/detail/:interventionId
		// /case-management/centre-intervention/detail/:interventionId
		const marker = `/detail/${interventionId}`;
		const idx = path.indexOf(marker);
		if (idx !== -1) {
			return path.slice(0, idx);
		}
		return '/case-management/centre-intervention';
	};

	// The edit pages are mounted as children of this detail route, so they stay in
	// the section the user came from and the breadcrumbs keep the whole trail
	// (... > Intervention details > Edit intervention).
	const getEditRoute = (segment: 'edit' | 'edit-case-note') => {
		const path = location.pathname;
		const marker = `/detail/${interventionId}`;
		const idx = path.indexOf(marker);
		const base =
			idx !== -1
				? path.slice(0, idx + marker.length)
				: `/case-management/centre-intervention/detail/${interventionId}`;
		return `${base}/${segment}/${getInterventionPageParam()}`;
	};

	// Tells the form which detail page to return to after saving / cancelling.
	const getInterventionPageParam = () => {
		const path = location.pathname;
		if (path.startsWith('/member-management/member-list')) {
			return 'edit-member-intervention';
		}
		if (path.startsWith('/member-management/admission-request-list')) {
			return 'edit-admission-intervention';
		}
		if (path.startsWith('/case-management/intervention')) {
			return 'edit-my-intervention';
		}
		return 'edit-centre-intervention';
	};
	const [deleteIntervention] = useDeleteInterventionMutation();
	const [checkRevertStatus] = useLazyCheckInterventionRevertStatusQuery();
	const [draftIntervention] = useDraftInterventionMutation();
	const showModal = useModal();
	const { userInfo } = useGetUser();

	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getInterventionGenerateReport] =
		useGetInterventionGenerateReportMutation();

	const { data: details }: any = useGetInterventionDetailQuery(
		interventionId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const runDelete = async (revertStatus: boolean) => {
		const response = await deleteIntervention({
			id: details?.data.id || '',
			revertStatus,
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.enquiryRecordDeleteSuccess'), {
				variant: 'warning',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(getReturnRoute(), { replace: true });
		}
	};

	const handleDelete = async () => {
		// When this intervention promoted the member from "Pending intake" to
		// "Intake completed", the staff can choose to revert that on deletion.
		const check = await checkRevertStatus(details?.data.id || '');
		const canRevertStatus = check.data?.data === true;

		showModal((props: any) => (
			<DeleteInterventionDialog
				{...props}
				canRevertStatus={canRevertStatus}
				nature={details?.data.natureDesc}
				onDelete={runDelete}
			/>
		));
	};
	const handleDraft = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('case.revertDraft')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('case.revertDraftText')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await draftIntervention(details?.data.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.revertDraftSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.interventionDetails')}
					</Typography>
					<Box>
						{details?.data.caseNoteDetailPermission && (
							<Button
								endIcon={<FileDownloadOutlinedIcon />}
								onClick={async () => {
									const response = await getInterventionGenerateReport(
										details?.data.id
									);
									if ('data' in response) {
										getExportCustomerPDF(response?.data?.data.url);
									}
								}}
								variant='contained'
								sx={{ marginRight: '24px' }}
							>
								{t('button.exportAsPdf')}
							</Button>
						)}

						{userInfo?.permissionList?.includes(
							PermissionEnum.CaseManagementDeleteIntervention
						) && (
							<Button
								endIcon={<DeleteOutlinedIcon />}
								onClick={() => handleDelete()}
								variant='contained'
							>
								{t('button.delete')}
							</Button>
						)}
					</Box>
				</Box>

				<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
					<Box display='flex' justifyContent='space-between' marginBottom={2}>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('member.personalInformation')}
						</Typography>
					</Box>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={6}>
							<AppFieldView
								label={t('member.fileNo')}
								value={details?.data.fileNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.unit')}
								value={details?.data.branchName}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.name_en')}
								value={details?.data.nameEn}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.name_zh')}
								value={details?.data.nameZh}
							/>
						</Grid>
					</Grid>
				</Card>
				{userInfo?.permissionList?.includes(
					PermissionEnum.CaseManagementInterventionDetailsAllExcludingCaseNote
				) && (
					<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
						<Box display='flex' justifyContent='space-between' marginBottom={2}>
							<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
								{t('case.interventionInformation')}
							</Typography>
							{userInfo?.permissionList?.includes(
								PermissionEnum.CaseManagementEditIntervention
							) && (
								<Button
									onClick={() => navigate(getEditRoute('edit'))}
									variant='outlined'
								>
									{t('button.edit')}
								</Button>
							)}
						</Box>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('case.interventionWorkerField')}
									value={details?.data.interventionWorkerName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.submittedBy')}
									value={details?.data.submittedByName}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('case.date')}
									value={
										details?.data.interventionDate
											? dayjs(details?.data.interventionDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.startTime')}
									value={
										details?.data.interventionStartTime
											? dayjs(
													details?.data.interventionStartTime,
													API_TIME_FORMAT
												)
													.locale('en')
													.format(DISPLAY_TIME_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.endTime')}
									value={
										details?.data.interventionEndTime
											? dayjs(
													details?.data.interventionEndTime,
													API_TIME_FORMAT
												)
													.locale('en')
													.format(DISPLAY_TIME_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.nature')}
									value={details?.data.natureDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.additional')}
									value={details?.data.additionalDesc}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('case.familyInvolvedAlliedHealthServicesEscort')}
									value={
										details?.data.familyInvolvedAlliedHealthServicesEscortDesc
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.involvingFamiliesCarers')}
									value={details?.data.involvingFamilyCarersDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.involvingChildren')}
									value={details?.data.involvingChildrenDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.riskLevel')}
									value={details?.data.riskLevelDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.supportingStaff')}
									value={details?.data.supportingStaffDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.linkUpMHS')}
									value={details?.data.linkUpToMhsDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.followUpClinic')}
									value={details?.data.followUpClinicDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.linkUpSSN')}
									value={details?.data.linkUpToSsnDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.linkUpSocialServiceProject')}
									value={details?.data.linkUpSocialServiceProjectDesc}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('case.linkUpSocialServiceProjectSpecify')}
									value={details?.data.linkUpSocialServiceOtProjectSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.relativesFamiliesCarersSession')}
									value={
										details?.data.relativesFamiliesCarersSession ? 'Yes' : 'No'
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.newCaseInvolvedFamilyInYear')}
									value={
										details?.data.newCaseInvolvedFamilyInYear ? 'Yes' : 'No'
									}
								/>
							</Grid>
						</Grid>
					</Card>
				)}
				{details?.data.caseNoteDetailPermission && (
					<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
						<Box display='flex' justifyContent='space-between' marginBottom={2}>
							<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
								{t('case.caseNote')}
							</Typography>
							{userInfo?.permissionList?.includes(
								PermissionEnum.CaseManagementCaseNoteChangeStatusRevertToDraft
							) &&
								details?.data.caseNoteStatus ==
									CaseNoteStatusEnum.Submitted && (
									<Button
										endIcon={<RestartAltOutlinedIcon />}
										onClick={() => handleDraft()}
										variant='outlined'
									>
										{t('case.revertDraft')}
									</Button>
								)}
							{userInfo?.permissionList?.includes(
								PermissionEnum.CaseManagementEditCaseNote
							) &&
								details?.data.caseNoteStatus !=
									CaseNoteStatusEnum.Submitted && (
									<Button
										onClick={() => navigate(getEditRoute('edit-case-note'))}
										variant='outlined'
									>
										{t('button.edit')}
									</Button>
								)}
						</Box>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('case.caseNoteType')}
									value={details?.data.caseNoteTypeDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('case.caseNoteStatus')}
									value={details?.data.caseNoteStatusDesc}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('case.caseNoteDetails')}
									value={details?.data.caseNoteDetails}
								/>
							</Grid>
							<Grid size={6}>
								<Box
									onClick={async () => {
										getExportCustomerPDF(details?.data.filePath);
									}}
									sx={{
										cursor: 'pointer',
									}}
								>
									<AppFieldView
										label={t('preAdmission.fileName')}
										value={details?.data.fileName}
										valueElement={
											<Box sx={{ color: '#1BBED7' }}>
												{details?.data.fileName}
											</Box>
										}
									/>
								</Box>
							</Grid>
						</Grid>
					</Card>
				)}

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
